import { Rectangle } from 'pixi.js';
import { assertNumber, assertMinimum, assertIsValidAlpha } from '../../validators';
import type { Point } from '../../types';
import type { StencilMaskSettings } from './StencilMask';
import { StencilMask } from './StencilMask';

type CircleMaskMorphState = {
    startRadius: number;
    endRadius: number;
    startCenter: Point;
    endCenter: Point;
};

export class CircleMask extends StencilMask {
    override _classType = 'CircleMask';

    #fillAlpha: number = 1;
    #lineThickness: number = 0;
    #startRadius: number = 0;
    #endRadius: number = 0.5;
    #centerX: number = 0.5;
    #centerY: number = 0.5;

    #morphState: CircleMaskMorphState | null = null;

    constructor(settings: StencilMaskSettings) {
        super(settings);

        const resolution = this.root.referenceResolution;

        this.#centerX = resolution.width * 0.5;
        this.#centerY = resolution.height * 0.5;
    }

    get fillAlpha(): number {
        return this.#fillAlpha;
    }

    set fillAlpha(value: number) {
        if (this.fillAlpha === value) {
            return;
        }
        assertIsValidAlpha(value);
        this.#fillAlpha = value;
        this.requiresUpdate = true;
    }

    get lineThickness(): number {
        return this.#lineThickness;
    }

    set lineThickness(value: number) {
        if (this.lineThickness === value) {
            return;
        }
        assertNumber(value);
        this.#lineThickness = value;
        this.requiresUpdate = true;
    }

    get startRadius(): number {
        return this.#startRadius;
    }

    set startRadius(value: number) {
        if (this.startRadius === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#startRadius = value;
        this.requiresUpdate = true;
    }

    get endRadius(): number {
        return this.#endRadius;
    }

    set endRadius(value: number) {
        if (this.#endRadius === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#endRadius = value;
        this.requiresUpdate = true;
    }

    set centerX(value: number) {
        if (this.#centerX === value) {
            return;
        }
        assertNumber(value);
        this.#centerX = value;
        this.requiresUpdate = true;
    }

    get centerX(): number {
        return this.#centerX;
    }

    set centerY(value: number) {
        if (this.#centerY === value) {
            return;
        }
        assertNumber(value);
        this.#centerY = value;
        this.requiresUpdate = true;
    }

    get centerY(): number {
        return this.#centerY;
    }

    override getMaxBounds(): Rectangle {
        if (this.#morphState) {
            const { startRadius, endRadius, startCenter, endCenter } = this.#morphState;
            const t = this.transition;
            const centerX = startCenter.x + (endCenter.x - startCenter.x) * t;
            const centerY = startCenter.y + (endCenter.y - startCenter.y) * t;
            const radius = startRadius + (endRadius - startRadius) * t;
            return new Rectangle(centerX - radius, centerY - radius, radius * 2, radius * 2);
        }

        const maxRadius = Math.max(this.#startRadius, this.#endRadius);
        return new Rectangle(
            this.#centerX - maxRadius,
            this.#centerY - maxRadius,
            maxRadius * 2,
            maxRadius * 2,
        );
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'centerX',
            'centerY',
            'startRadius',
            'endRadius',
            'lineThickness',
            'fillAlpha',
        ]);
    }

    override _update(): void {
        const { centerX, centerY, radius } = (() => {
            if (this.#morphState) {
                const { startRadius, endRadius, startCenter, endCenter } = this.#morphState;
                const t = this.transition;
                return {
                    centerX: startCenter.x + (endCenter.x - startCenter.x) * t,
                    centerY: startCenter.y + (endCenter.y - startCenter.y) * t,
                    radius: startRadius + (endRadius - startRadius) * t,
                };
            }
            return {
                centerX: this.#centerX,
                centerY: this.#centerY,
                radius: this.#startRadius + (this.#endRadius - this.#startRadius) * this.transition,
            };
        })();

        this.clear();
        this.lineStyle(this.#lineThickness, 0xffffff, 1);
        this.beginFill(0xffffff, this.#fillAlpha);
        this.drawCircle(centerX, centerY, radius);
        this.endFill();
    }

    /**
     * @internal
     */
    _setMorphState(state: CircleMaskMorphState): void {
        this.#morphState = state;
        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    _clearMorphState(): void {
        this.#morphState = null;
        this.requiresUpdate = true;
    }
}
